import { useState } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { FileText, Download, ShieldOff, BadgeCheck, ScrollText, Eye } from 'lucide-react';
import { downloadPDF, previewPDF } from '../lib/pdf';

const LETTERS = [
  {
    kind: 'termination',
    title: 'Termination Letter',
    desc: 'Formal notice of termination with effective date, reason and next-steps.',
    icon: ShieldOff,
    accent: 'text-red-600',
    bg: 'bg-red-50',
  },
  {
    kind: 'relieving',
    title: 'Relieving Letter',
    desc: 'Confirms the employee has been duly relieved post-clearance.',
    icon: BadgeCheck,
    accent: 'text-emerald-600',
    bg: 'bg-emerald-50',
  },
  {
    kind: 'experience',
    title: 'Experience Certificate',
    desc: 'To-Whom-It-May-Concern certificate stating tenure, designation and contact details.',
    icon: ScrollText,
    accent: 'text-[#3D4A8E]',
    bg: 'bg-[#3D4A8E]/10',
  },
];

export default function OffboardingLettersSection({ employeeId, employeeName, terminated }) {
  const [busy, setBusy] = useState(null);

  if (!terminated) return null;

  const labelMap = { termination: 'Termination_Letter', relieving: 'Relieving_Letter', experience: 'Experience_Letter' };

  const onDownload = async (kind) => {
    setBusy(`dl-${kind}`);
    const safe = (employeeName || 'employee').replace(/\s+/g, '_');
    await downloadPDF(`/employees/${employeeId}/letters/${kind}.pdf`, `${labelMap[kind]}_${safe}.pdf`);
    setBusy(null);
  };
  const onPreview = async (kind) => {
    setBusy(`pv-${kind}`);
    await previewPDF(`/employees/${employeeId}/letters/${kind}.pdf`);
    setBusy(null);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="offboarding-letters-section">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
          <FileText className="w-5 h-5 text-slate-500" />
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Offboarding</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">Service letters</h2>
          <p className="text-xs text-slate-500 mt-1">Officially generated PDF letters. Preview in browser or download.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {LETTERS.map(({ kind, title, desc, icon: Icon, accent, bg }) => (
          <div key={kind} data-testid={`letter-card-${kind}`}
               className="bg-slate-50/60 border border-slate-200 rounded-xl p-4 flex flex-col gap-3">
            <div className={`w-9 h-9 rounded-lg ${bg} ${accent} flex items-center justify-center`}>
              <Icon className="w-4 h-4" strokeWidth={1.8} />
            </div>
            <div className="flex-1">
              <div className="font-heading font-semibold text-slate-900 text-sm">{title}</div>
              <p className="text-xs text-slate-500 mt-1 leading-snug">{desc}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" data-testid={`preview-${kind}`}
                      onClick={() => onPreview(kind)} disabled={!!busy}
                      className="rounded-full flex-1">
                <Eye className="w-3.5 h-3.5 mr-1.5" /> {busy === `pv-${kind}` ? '…' : 'Preview'}
              </Button>
              <Button size="sm" data-testid={`download-${kind}`}
                      onClick={() => onDownload(kind)} disabled={!!busy}
                      className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] flex-1">
                <Download className="w-3.5 h-3.5 mr-1.5" /> {busy === `dl-${kind}` ? '…' : 'PDF'}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
